import React, { useState } from "react";
import { Box, Button, Card, CardContent, Grid, Typography, TextField,
  MenuItem, IconButton, Select, FormControl, InputLabel, Paper, Checkbox,
  Snackbar, Alert, Backdrop, CircularProgress, Radio
} from "@mui/material";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import { KeyboardArrowUp, KeyboardArrowDown, Delete, CloudUpload, RadioButtonChecked, CheckBox as CheckBoxIcon, Close } from "@mui/icons-material";
import { useDropzone } from "react-dropzone";
import { useNavigate, useParams } from "react-router-dom";
import { db, storageRef } from "../../Environment/firebase";
import { departments } from "../../Environment/Config";
import { v4 as uuidv4 } from "uuid";
import firebase from "firebase/compat/app";

type QuizOption = { text: string; isCorrect: boolean; id?: string };

type QuizItemForm = {
  id: string;
  type: "single_choice" | "multiple_choice";
  questionText: string;
  options: QuizOption[];
};

type FormValues = {
  status: "draft" | "published";
  caseInfo: {
    age: number;
    gender: string;
    history: string;
  };
  tags: string;
  departmentTags: string[];
  images: { file?: File; id: string; preview: string; isExisting?: boolean; path?: string; name?: string }[];
  quizItems: QuizItemForm[];
  closing: {
    explanation: string;
    referenceUrl: string;
  };
};

export default function MStudyForm() {
  const [isSaving, setIsSaving] = useState(false);
  const [existingQuestionNumber, setExistingQuestionNumber] = useState<number | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: "success" | "error" | "info" | "warning" }>({
    open: false,
    message: "",
    severity: "info",
  });
  const handleCloseSnackbar = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === "clickaway") return;
    setSnackbar(prev => ({ ...prev, open: false }));
  };
  const { ActionKind, ObjectId } = useParams();
  const navigate = useNavigate();
  // URLが /mypage/mstudy/:id/edit の場合、ActionKindがID、ObjectIdが"edit"になります。
  const editId = ObjectId === "edit" ? ActionKind : null;
  const isEditMode = !!editId;

  const { control, handleSubmit, reset, watch, setValue } = useForm<FormValues>({
    defaultValues: {
      status: "draft",
      caseInfo: { age: 0, gender: "male", history: "" },
      tags: "",
      departmentTags: [],
      images: [],
      quizItems: [
        {
          id: uuidv4(),
          type: "single_choice",
          questionText: "",
          options: [{ text: "", isCorrect: false }],
        },
      ],
      closing: { explanation: "", referenceUrl: "" },
    },
  });

  const { fields: imageFields, append: appendImage, move: moveImage, remove: removeImage } = useFieldArray({
    control,
    name: "images",
    keyName: "fieldId",
  });

  const { fields: quizFields, append: appendQuiz, remove: removeQuiz, move: moveQuiz } = useFieldArray({
    control,
    name: "quizItems",
    keyName: "fieldId",
  });

  const onDrop = React.useCallback((acceptedFiles: File[]) => {
    acceptedFiles.forEach((file) => {
      appendImage({
        file,
        id: uuidv4(),
        preview: URL.createObjectURL(file),
      });
    });
  }, [appendImage]);

  const { getRootProps, getInputProps } = useDropzone({ onDrop, accept: { 'image/*': [] } });

  React.useEffect(() => {
    if (isEditMode && editId) {
      const fetchQuestion = async () => {
        try {
          const doc = await db.collection("questions").doc(editId).get();
          if (doc.exists) {
            const data = doc.data() as any;
            if (data.questionNumber) setExistingQuestionNumber(data.questionNumber);

            const resolvedImages = await Promise.all((data.imageUrls || []).map(async (urlPath: string) => {
              let previewUrl = urlPath;
              if (!urlPath.startsWith("http")) { // Storageのパス形式の場合
                try {
                  previewUrl = await storageRef.child(urlPath).getDownloadURL();
                } catch (e) {
                  console.warn("画像の取得に失敗しました:", urlPath);
                }
              }
              let filename = "登録済み画像";
              try {
                // URLやパスから元のファイル名らしきものを抽出（UUID部分を除去）
                let decoded = decodeURIComponent(urlPath).split("?")[0];
                let baseName = decoded.split("/").pop() || "登録済み画像";
                const parts = baseName.split("_");
                if (parts.length > 1 && parts[0].length > 30) {
                  parts.shift();
                  filename = parts.join("_");
                } else {
                  filename = baseName;
                }
              } catch (e) {}

              return {
                id: uuidv4(),
                preview: previewUrl,
                path: urlPath,
                isExisting: true,
                name: filename,
              };
            }));

            reset({
              status: data.status || "draft",
              caseInfo: data.caseInfo || { age: 0, gender: "male", history: "" },
              tags: data.tags ? data.tags.join(", ") : "",
              departmentTags: data.departmentTags || [],
              images: resolvedImages,
              quizItems: (data.quizItems || []).map((item: any) => ({
                id: item.id || uuidv4(),
                type: item.type || "single_choice",
                questionText: item.questionText || "",
                options: item.options.map((text: string, index: number) => ({
                  text,
                  isCorrect: item.correctAnswers.includes(index),
                }))
              })),
              closing: data.closing || { explanation: "", referenceUrl: "" },
            });
          }
        } catch (error) {
          console.error("Error fetching document:", error);
        }
      };
      fetchQuestion();
    }
  }, [isEditMode, editId, reset]);

  const onSubmit = async (data: FormValues) => {
    setIsSaving(true);
    setSnackbar({ open: false, message: "", severity: "info" });
    try {
      const docRef = isEditMode && editId 
        ? db.collection("questions").doc(editId) 
        : db.collection("questions").doc();
      const docId = docRef.id;

      // 1. Upload Images
      const imageUrls: string[] = [];
      for (const img of data.images) {
        if (img.isExisting) {
          // 既存画像はこれまで通りならURL、新しい形式ならパスを使用
          imageUrls.push(img.path || img.preview);
        } else if (img.file) {
          // 新規画像はIDと一意のUUID、ファイル名でパスを作成し、アップロード
          const filePath = `mstudy/${docId}/${uuidv4()}_${img.file.name}`;
          const fileRef = storageRef.child(filePath);
          await fileRef.put(img.file);
          imageUrls.push(filePath);
        }
      }

      // 2. Format Data for Firestore
      const processedQuizItems = data.quizItems.map(quiz => {
        const correctAnswers = quiz.options
          .map((opt, index) => opt.isCorrect ? index : -1)
          .filter(index => index !== -1);

        return {
          id: quiz.id,
          type: quiz.type,
          questionText: quiz.questionText,
          options: quiz.options.map(opt => opt.text),
          correctAnswers,
        };
      });

      const tagsArray = data.tags.split(",").map(s => s.trim()).filter(s => s !== "");
      const departmentTagsArray = data.departmentTags;

      // 公開ステータスで、まだ番号がない場合のみ採番
      let questionNumber = existingQuestionNumber;
      if (data.status === "published" && !questionNumber) {
        const snapshot = await db.collection("questions")
          .orderBy("questionNumber", "desc")
          .limit(1)
          .get();
        const maxNumber = snapshot.empty ? 0 : (snapshot.docs[0].data().questionNumber || 0);
        questionNumber = maxNumber + 1;
      }

      const docData: any = {
        status: data.status,
        ...(questionNumber !== null && { questionNumber }),
        updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
        caseInfo: {
          age: Number(data.caseInfo.age),
          gender: data.caseInfo.gender,
          history: data.caseInfo.history,
        },
        tags: tagsArray,
        departmentTags: departmentTagsArray,
        imageUrls,
        quizItems: processedQuizItems,
        closing: {
          explanation: data.closing.explanation,
          referenceUrl: data.closing.referenceUrl,
        },
      };

      if (!isEditMode) {
        docData.createdAt = firebase.firestore.FieldValue.serverTimestamp();
      }

      // 3. Save to Firestore
      if (isEditMode && editId) {
        await docRef.update(docData);
        setSnackbar({ open: true, message: "問題を更新しました。", severity: "success" });
      } else {
        await docRef.set(docData);
        setSnackbar({ open: true, message: "問題が正常に作成されました。", severity: "success" });
        reset();
      }
      
      // 保存後、1秒待って一覧画面に戻る
      setTimeout(() => {
        window.scrollTo(0, 0);
        navigate("/mypage/mstudy");
      }, 1000);
    } catch (error) {
      console.error("Error saving M-Study:", error);
      setSnackbar({ open: true, message: "エラーが発生しました。時間を置いてもう一度お試しください。", severity: "error" });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Box sx={{ mt: 3, ml: 2, p: 2, maxWidth: 1000, margin: "auto", textAlign: "left", position: "relative" }}>
      <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
        {isEditMode ? "M-Study 問題の編集" : "M-Study 新規問題作成"}
      </Typography>

      <form onSubmit={handleSubmit(onSubmit)}>
        {/* 基本情報 */}
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Grid container justifyContent="space-between" alignItems="center">
              <Grid item><Typography variant="h6" gutterBottom>症例基本情報</Typography></Grid>
              <Grid item xs={4} sm={3}>
                <Controller
                  name="status"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth size="small">
                      <InputLabel>公開ステータス</InputLabel>
                      <Select {...field} label="公開ステータス">
                        <MenuItem value="draft">下書き</MenuItem>
                        <MenuItem value="published">公開する</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>
            </Grid>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={6} sm={4}>
                <Controller
                  name="caseInfo.age"
                  control={control}
                  render={({ field }) => <TextField {...field} label="年齢" type="number" fullWidth />}
                />
              </Grid>
              <Grid item xs={6} sm={4}>
                <Controller
                  name="caseInfo.gender"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel>性別</InputLabel>
                      <Select {...field} label="性別">
                        <MenuItem value="male">男性</MenuItem>
                        <MenuItem value="female">女性</MenuItem>
                        <MenuItem value="other">その他</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="caseInfo.history"
                  control={control}
                  render={({ field }) => <TextField {...field} label="主訴・症状" fullWidth multiline rows={2} />}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="tags"
                  control={control}
                  render={({ field }) => <TextField {...field} label="タグ (カンマ区切り)" fullWidth placeholder="CT, 急性腹症" />}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="departmentTags"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel>診療科タグ</InputLabel>
                      <Select
                        {...field}
                        multiple
                        label="診療科タグ"
                        renderValue={(selected) => (selected as string[]).join(", ")}
                      >
                        {departments.map((dept) => (
                          <MenuItem key={dept} value={dept}>
                            <Checkbox checked={(field.value as string[]).includes(dept)} />
                            {dept}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* 画像アップロード */}
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>画像データ</Typography>
            
            <Paper
              {...getRootProps()}
              variant="outlined"
              sx={{ p: 3, textAlign: "center", cursor: "pointer", bgcolor: "#f9f9f9", mb: 2 }}
            >
              <input {...getInputProps()} />
              <CloudUpload sx={{ fontSize: 48, color: "gray" }} />
              <Typography color="textSecondary">ここに画像をドラッグ＆ドロップするか、クリックして選択</Typography>
            </Paper>

            {imageFields.map((field, index) => (
              <Box key={field.fieldId} sx={{ display: "flex", alignItems: "center", mb: 1, p: 1, border: "1px solid #ddd", borderRadius: 1 }}>
                <img src={field.preview} alt="preview" style={{ width: 80, height: 80, objectFit: "cover", marginRight: 16 }} />
                <Typography sx={{ flexGrow: 1 }}>
                  {field.isExisting ? (field.name || "登録済み画像") : field.file?.name}
                </Typography>
                <Box>
                  <IconButton onClick={() => moveImage(index, index - 1)} disabled={index === 0}>
                    <KeyboardArrowUp />
                  </IconButton>
                  <IconButton onClick={() => moveImage(index, index + 1)} disabled={index === imageFields.length - 1}>
                    <KeyboardArrowDown />
                  </IconButton>
                  <IconButton color="error" onClick={() => removeImage(index)}>
                    <Delete />
                  </IconButton>
                </Box>
              </Box>
            ))}
          </CardContent>
        </Card>

        {/* 設問リスト */}
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>設問リスト</Typography>
            {quizFields.map((quiz, qIndex) => (
              <Card key={quiz.fieldId} variant="outlined" sx={{ mb: 2, p: 2 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    問題 {qIndex + 1}
                  </Typography>
                  <Box>
                    <IconButton onClick={() => moveQuiz(qIndex, qIndex - 1)} disabled={qIndex === 0}>
                      <KeyboardArrowUp />
                    </IconButton>
                    <IconButton onClick={() => moveQuiz(qIndex, qIndex + 1)} disabled={qIndex === quizFields.length - 1}>
                      <KeyboardArrowDown />
                    </IconButton>
                    <IconButton color="error" onClick={() => removeQuiz(qIndex)}>
                      <Delete />
                    </IconButton>
                  </Box>
                </Box>
                <Grid container spacing={2} alignItems="flex-start">
                  <Grid item xs={12} sm={8}>
                    <Controller
                      name={`quizItems.${qIndex}.questionText`}
                      control={control}
                      render={({ field }) => <TextField {...field} label="問題文" fullWidth multiline />}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Controller
                      name={`quizItems.${qIndex}.type`}
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth>
                          <InputLabel>種類</InputLabel>
                          <Select
                            {...field}
                            label="種類"
                            renderValue={(value) => (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {value === 'single_choice' ? <RadioButtonChecked fontSize="small" color="action" /> : <CheckBoxIcon fontSize="small" color="action" />}
                                {value === 'single_choice' ? 'ラジオボタン' : 'チェックボックス'}
                              </Box>
                            )}
                          >
                            <MenuItem value="single_choice">
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <RadioButtonChecked fontSize="small" color="action" />
                                ラジオボタン
                              </Box>
                            </MenuItem>
                            <MenuItem value="multiple_choice">
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CheckBoxIcon fontSize="small" color="action" />
                                チェックボックス
                              </Box>
                            </MenuItem>
                          </Select>
                        </FormControl>
                      )}
                    />
                  </Grid>
                </Grid>
                
                <Box sx={{ mt: 2, ml: 2 }}>
                  <Typography variant="subtitle2">選択肢 (正解にチェック)</Typography>
                  <QuizOptionsArray control={control} watch={watch} setValue={setValue} qIndex={qIndex} />
                </Box>
              </Card>
            ))}
            <Button variant="outlined" onClick={() => appendQuiz({
              id: uuidv4(),
              type: "single_choice",
              questionText: "",
              options: [{ text: "", isCorrect: false }]
            })}>
              問題を追加
            </Button>
          </CardContent>
        </Card>

        {/* 解説・出典 */}
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>解説・出典</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Controller
                  name="closing.explanation"
                  control={control}
                  render={({ field }) => <TextField {...field} label="解説" fullWidth multiline rows={3} />}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="closing.referenceUrl"
                  control={control}
                  render={({ field }) => <TextField {...field} label="参考URL" fullWidth />}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Box textAlign="center" sx={{ display: "flex", justifyContent: "center", gap: 2 }}>
          <Button variant="outlined" color="primary" size="large" onClick={() => { window.scrollTo(0, 0); navigate("/mypage/mstudy"); }} disabled={isSaving}>
            一覧に戻る
          </Button>
          <Button type="submit" variant="contained" color="primary" size="large" disabled={isSaving} startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : null}>
            {isSaving ? "保存中..." : isEditMode ? "変更を保存" : "保存する"}
          </Button>
        </Box>
      </form>

      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={4000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%', boxShadow: 3 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

// 選択肢の配列を管理するサブコンポーネント
function QuizOptionsArray({ control, watch, setValue, qIndex }: { control: any, watch: any, setValue: any, qIndex: number }) {
  const { fields, append, remove, insert } = useFieldArray({
    control,
    name: `quizItems.${qIndex}.options`,
    keyName: "fieldId",
  });

  const quizType = watch(`quizItems.${qIndex}.type`);

  return (
    <Box>
      {fields.map((opt, oIndex) => (
        <Box key={opt.fieldId} sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <Controller
            name={`quizItems.${qIndex}.options.${oIndex}.isCorrect`}
            control={control}
            render={({ field }) => (
              quizType === "single_choice" ? (
                <Radio
                  checked={field.value}
                  onChange={(e) => {
                    if (e.target.checked) {
                      fields.forEach((_, i) => {
                        setValue(`quizItems.${qIndex}.options.${i}.isCorrect`, i === oIndex, { shouldDirty: true });
                      });
                    }
                  }}
                  color="primary"
                />
              ) : (
                <Checkbox
                  checked={field.value}
                  onChange={(e) => field.onChange(e.target.checked)}
                  color="primary"
                />
              )
            )}
          />
          <Controller
            name={`quizItems.${qIndex}.options.${oIndex}.text`}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                inputProps={{ id: `option-input-${qIndex}-${oIndex}` }}
                placeholder={`選択肢 ${oIndex + 1}`}
                size="small"
                variant="standard"
                sx={{ flexGrow: 1, mr: 1 }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    // 変換の確定エンター（IME入力中）なら追加処理を行わずに無視する
                    if (e.nativeEvent.isComposing) return;
                    
                    // フォーム自体の送信を防ぎつつ、入力中の選択肢の「真下」に新規行を追加
                    e.preventDefault();
                    insert(oIndex + 1, { text: "", isCorrect: false });
                    // 入力欄がレンダリングされた直後に新しく追加されたフィールドへフォーカスを移動
                    setTimeout(() => {
                      const nextInput = document.getElementById(`option-input-${qIndex}-${oIndex + 1}`);
                      if (nextInput) nextInput.focus();
                    }, 50);
                  }
                }}
              />
            )}
          />
          <IconButton sx={{ color: 'action.active' }} onClick={() => remove(oIndex)} disabled={fields.length === 1}>
            <Close />
          </IconButton>
        </Box>
      ))}
      <Button size="small" onClick={() => append({ text: "", isCorrect: false })}>選択肢を追加</Button>
    </Box>
  );
}
